import React, { useEffect, useState } from "react";
import CrmLayout from "../../components/CrmLayout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { fmtDate, prettyDate, money } from "../../lib/format";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import WeekCalendar from "../../components/WeekCalendar";
import { Bell, Plus, CalendarIcon, List, CalendarRange } from "lucide-react";
import { providerLabel } from "../../lib/industry";

const STATUSES = ["scheduled", "confirmed", "completed", "cancelled", "no_show"];
const STATUS_STYLE = {
  scheduled: "bg-secondary text-secondary-foreground", confirmed: "bg-primary/10 text-primary",
  completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]", cancelled: "bg-destructive/10 text-destructive",
  no_show: "bg-muted text-muted-foreground", pending_deposit: "bg-accent/20 text-accent-foreground",
};

export default function Appointments() {
  const { user, salon } = useAuth();
  const [appts, setAppts] = useState([]);
  const [view, setView] = useState("list");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDate, setFilterDate] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    const params = {};
    if (view === "list") {
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterDate) params.date = filterDate;
    }
    api.get("/appointments", { params }).then((r) => setAppts(r.data)).catch(() => {});
  };
  useEffect(load, [filterStatus, filterDate, view]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}/status`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendReminder = async (id) => {
    try { await api.post(`/appointments/${id}/send-reminder`); toast.success("Reminder sent"); }
    catch { toast.error("Could not send reminder"); }
  };
  const reschedule = async (id, date, start_time) => {
    try { await api.patch(`/appointments/${id}/reschedule`, { date, start_time }); toast.success("Rescheduled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <CrmLayout title="Appointments" actions={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md" data-testid="add-appointment-btn"><Plus size={16} className="mr-1" /> New appointment</Button></DialogTrigger>
        <NewAppointmentDialog onDone={() => { setOpen(false); load(); }} />
      </Dialog>
    }>
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <div className="flex rounded-md border border-border overflow-hidden bg-card">
          <button data-testid="view-list" onClick={() => setView("list")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><List size={15} /> List</button>
          <button data-testid="view-week" onClick={() => setView("week")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${view === "week" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><CalendarRange size={15} /> Week</button>
        </div>
        {view === "list" && <>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44 bg-card" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-44 bg-card" data-testid="filter-date" />
          {filterDate && <Button variant="ghost" onClick={() => setFilterDate("")}>Clear</Button>}
        </>}
      </div>

      {view === "week" ? (
        <WeekCalendar appointments={appts} workStart={salon?.settings?.work_start ?? 9} workEnd={salon?.settings?.work_end ?? 18} onReschedule={reschedule} />
      ) : (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date / Time</TableHead><TableHead>Client</TableHead><TableHead>Service</TableHead>
            {user?.role !== "stylist" && <TableHead>{providerLabel(salon?.business_type)}</TableHead>}<TableHead>Status</TableHead><TableHead className="text-right">Remind</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {appts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No appointments found.</TableCell></TableRow>}
            {appts.map((a) => (
              <TableRow key={a.id} data-testid={`appt-row-${a.id}`}>
                <TableCell><div className="text-sm">{prettyDate(a.date)}</div><div className="text-xs text-muted-foreground font-mono">{a.start_time}–{a.end_time}</div></TableCell>
                <TableCell><div className="text-sm font-medium">{a.client_name}</div><div className="text-xs text-muted-foreground">{a.client_phone || a.client_email}</div></TableCell>
                <TableCell className="text-sm">{a.service_name} <span className="text-muted-foreground">· {money(a.price)}</span>{a.deposit?.required && <div className="text-xs text-muted-foreground">deposit {a.deposit.status}</div>}</TableCell>
                {user?.role !== "stylist" && <TableCell className="text-sm">{a.stylist_name}</TableCell>}
                <TableCell>
                  <Select value={STATUSES.includes(a.status) ? a.status : "scheduled"} onValueChange={(v) => setStatus(a.id, v)}>
                    <SelectTrigger className="w-36 h-8 bg-card" data-testid={`status-select-${a.id}`}><Badge className={STATUS_STYLE[a.status]}>{a.status.replace("_", " ")}</Badge></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="sm" data-testid={`remind-${a.id}`} onClick={() => sendReminder(a.id)}><Bell size={15} /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      )}
    </CrmLayout>
  );
}

function NewAppointmentDialog({ onDone }) {
  const { salon } = useAuth();
  const [services, setServices] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", service_id: "", stylist_id: "", start_time: "" });
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/stylists").then((r) => setStylists(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (form.stylist_id && date && form.service_id)
      api.get("/availability", { params: { stylist_id: form.stylist_id, date: fmtDate(date), service_id: form.service_id } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [form.stylist_id, date, form.service_id]);

  const submit = async () => {
    setSaving(true);
    try { await api.post("/appointments", { ...form, date: fmtDate(date) }); toast.success("Appointment created"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const valid = form.client_name && form.service_id && form.stylist_id && date && form.start_time;

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle className="font-display text-xl font-semibold">New appointment</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Client name</Label><Input data-testid="new-client-name" className="mt-1" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} /></div>
        <div><Label>Email</Label><Input data-testid="new-client-email" className="mt-1" value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} /></div>
        <div><Label>Phone</Label><Input data-testid="new-client-phone" className="mt-1" value={form.client_phone} onChange={(e) => setForm((f) => ({ ...f, client_phone: e.target.value }))} /></div>
        <div><Label>Service</Label>
          <Select value={form.service_id} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v }))}>
            <SelectTrigger className="mt-1" data-testid="new-service"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>{providerLabel(salon?.business_type)}</Label>
          <Select value={form.stylist_id} onValueChange={(v) => setForm((f) => ({ ...f, stylist_id: v }))}>
            <SelectTrigger className="mt-1" data-testid="new-stylist"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{stylists.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Date</Label>
          <Popover><PopoverTrigger asChild><Button variant="outline" className="mt-1 w-full justify-start font-normal" data-testid="new-date"><CalendarIcon size={15} className="mr-2" />{date ? prettyDate(fmtDate(date)) : "Pick a date"}</Button></PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} /></PopoverContent></Popover></div>
        <div><Label>Time</Label>
          <Select value={form.start_time} onValueChange={(v) => setForm((f) => ({ ...f, start_time: v }))} disabled={!slots.length}>
            <SelectTrigger className="mt-1" data-testid="new-time"><SelectValue placeholder={slots.length ? "Select" : "Pick date first"} /></SelectTrigger>
            <SelectContent>{slots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <DialogFooter><Button data-testid="new-appt-save" disabled={!valid || saving} onClick={submit} className="bg-primary text-primary-foreground hover:bg-primary/90">{saving ? "Saving…" : "Create"}</Button></DialogFooter>
    </DialogContent>
  );
}
